import { useCallback } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { getEmployeeList } from '@/api/employee';
import type {
  GetEmployeeListParams,
  GetEmployeeListResponse,
} from '@/types/employee';
import { useAuth } from '@/context/AuthContext';
import { mergeQueryOptions } from '@/utility/queryOptions';
import { QueryKeys } from '@gokrew/models/constants';
import {
  fetchAllPages,
  fetchByIdChunks,
} from '@gokrew/ui/utility/fetchAllPages';

export function useEmployees(
  params: GetEmployeeListParams,
  options: Omit<
    UseQueryOptions<GetEmployeeListResponse>,
    'queryKey' | 'queryFn'
  > = {}
) {
  const { companyId } = useAuth();
  return useQuery<GetEmployeeListResponse>({
    queryKey: [QueryKeys.Employees, params],
    queryFn: () => getEmployeeList(companyId!, params),
    placeholderData: keepPreviousData,
    ...mergeQueryOptions(options, [!!companyId]),
  });
}

export const useInfiniteEmployees = (
  params: Omit<GetEmployeeListParams, 'page'>,
  options: { enabled?: boolean } = {}
) => {
  const { companyId, branchId: authBranchId } = useAuth();
  const effectiveBranchId = params.branchId ?? authBranchId;
  return useInfiniteQuery({
    queryKey: [QueryKeys.Employees, 'infinite', params, effectiveBranchId],
    queryFn: ({ pageParam }) =>
      getEmployeeList(companyId!, {
        ...params,
        page: pageParam,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.hasNextPage ? lastPage.data.meta.page + 1 : undefined,
    enabled: !!companyId && (options.enabled ?? true),
  });
};

/**
 * The employees with these ids, whatever their page in the list: names for a
 * saved selection or a summary. Never the first page standing in for them.
 */
export const useEmployeesByIds = (
  ids: string[],
  options: { enabled?: boolean } = {}
) => {
  const { companyId } = useAuth();
  const sorted = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [QueryKeys.Employees, 'byIds', sorted],
    queryFn: () =>
      fetchByIdChunks(sorted, async (employeeIds, limit) => {
        const res = await getEmployeeList(companyId!, {
          employeeIds,
          page: 1,
          limit,
        });
        return res.data.data;
      }),
    enabled: !!companyId && sorted.length > 0 && (options.enabled ?? true),
    placeholderData: keepPreviousData,
  });
};

/**
 * Every employee matching a list filter, page after page, for a one-off
 * action such as "Select all" (not for rendering: use the infinite list).
 * Scoped like `useInfiniteEmployees`, so it matches the rows on screen.
 */
export const useFetchAllEmployees = () => {
  const queryClient = useQueryClient();
  const { companyId, branchId: authBranchId } = useAuth();
  return useCallback(
    async (params: Omit<GetEmployeeListParams, 'page' | 'limit'>) => {
      const effectiveBranchId = params.branchId ?? authBranchId;
      const query = {
        ...params,
        ...(effectiveBranchId ? { branchId: effectiveBranchId } : {}),
      };
      const res = await queryClient.fetchQuery({
        queryKey: [QueryKeys.Employees, 'all', query],
        queryFn: () =>
          fetchAllPages((page, limit) =>
            getEmployeeList(companyId!, { ...query, page, limit })
          ),
      });
      return res.data.data;
    },
    [authBranchId, companyId, queryClient]
  );
};
